import React from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TimeConverter } from "../../services/utils/TimeConverter";
const timeConverter = new TimeConverter();
// attempt.date arrives as "DD/MM/YY" (e.g. "18/10/25"). Date.parse would read
// that as month/day and silently scramble the ordering, so parse it explicitly.
// Two-digit years are assumed to be 20xx, which holds fine until 2099.
const parseUkDate = (value) => {
    const [day, month, year] = value.split('/').map(Number);
    if (!day || !month || !year)
        return NaN;
    const fullYear = year < 100 ? 2000 + year : year;
    return new Date(fullYear, month - 1, day).getTime();
};
const renderTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0)
        return null;
    const point = payload[0].payload;
    return (React.createElement("div", { style: {
            background: 'var(--card-bg)',
            border: '1px solid var(--gray-100)',
            borderRadius: '4px',
            padding: '0.4rem 0.6rem',
            fontSize: '0.8rem',
        } },
        React.createElement("div", null,
            React.createElement("strong", null, point.time),
            " \u2022 ",
            point.date),
        point.meet && React.createElement("div", { style: { color: 'var(--gray-400)' } }, point.meet)));
};
const SwimTimeTrendChart = ({ attempts }) => {
    const points = [...attempts]
        .reverse()
        .map(attempt => ({
        date: attempt.date,
        timestamp: parseUkDate(attempt.date),
        seconds: timeConverter.parseTimeToSeconds(attempt.time),
        time: attempt.time,
        meet: attempt.meet,
    }))
        .filter(point => isFinite(point.seconds) && isFinite(point.timestamp))
        .map((point, idx) => ({ ...point, idx }));
    if (points.length < 2) {
        return null;
    }
    const seconds = points.map(p => p.seconds);
    const minSeconds = Math.min(...seconds);
    const maxSeconds = Math.max(...seconds);
    // Fixed buffer rather than a % of range, so a tiny spread (e.g. 0.2s)
    // still gets visible headroom instead of being squashed flat.
    const yBuffer = Math.max((maxSeconds - minSeconds) * 0.15, 0.3);
    // Track the personal best (fastest time) achieved before each point, so a 
    // segment can be judged against the running PB rather than just the previous
    // attempt. bestBefore[i] is the fastest of all the attempts strictly before i.
    const bestBefore = points.map(() => Infinity);
    for (let i = 1; i < points.length; i++) {
        bestBefore[i] = Math.min(bestBefore[i - 1], points[i - 1].seconds);
    }
    // Recharts draws a line as a single path with one stroke style, so to make a 
    // segment dashed (when the enddpoints is slower than the personal best so far - 
    // i.e. not a new PB) we render each segment as its own two-point line, keyed
    // seg0, seg1, ...  Each row carries a value only at the segment's two endpoints;
    // null elsewhere leaves gaps.
    const segmentCount = points.length - 1;
    const data = points.map((point, i) => {
        const row = { ...point };
        for (let s = 0; s < segmentCount; s++) {
            row[`seg${s}`] = i === s || i === s + 1 ? point.seconds : null;
        }
        return row;
    });
    // Sweep the segments left-to-right by staggering each ones start across the same
    // total window, so the split-into-segments line still animates as one continuous draw.
    const ANIM_MS = 1500;
    const segDuration = ANIM_MS / segmentCount;
    return (React.createElement("div", { style: { width: '100%', height: 180, marginBottom: '0.75rem' } },
        React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
            React.createElement(LineChart, { data: data, margin: { top: 8, right: 12, bottom: 0, left: 0 } },
                React.createElement(CartesianGrid, { strokeDasharray: "3 3", stroke: "var(--gray-100)", vertical: false }),
                React.createElement(XAxis, { dataKey: "timestamp", type: "number", scale: "time", domain: ['dataMin', 'dataMax'], tickFormatter: (value) => new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), tick: { fontSize: 11, fill: 'var(--gray-400)' }, axisLine: { stroke: 'var(--gray-100)' }, tickLine: false }),
                React.createElement(YAxis, { width: 48, tick: { fontSize: 11, fill: 'var(--gray-400)' }, axisLine: false, tickLine: false, tickFormatter: (value) => timeConverter.formatSecondsToTime(value), domain: [minSeconds - yBuffer, maxSeconds + yBuffer] }),
                React.createElement(Tooltip, { content: renderTooltip }),
                React.createElement(Line, { type: "linear", dataKey: "seconds", stroke: "transparent", animationDuration: ANIM_MS, strokeWidth: 2, dot: { r: 3, stroke: 'var(--primary)', fill: 'var(--primary)' }, activeDot: { r: 5 } }),
                points.slice(0, -1).map((_, s) => {
                    // Dashed when the segment's endpoint is slower than the 
                    // personal best achieved up to that point (not a new PB)
                    const slower = points[s + 1].seconds > bestBefore[s + 1];
                    return (React.createElement(Line, { key: `seg${s}`, type: "linear", dataKey: `seg${s}`, stroke: "var(--primary)", strokeWidth: 2, strokeDasharray: slower ? '5 4' : undefined, dot: false, activeDot: false, connectNulls: false, animationBegin: s * segDuration, animationDuration: segDuration }));
                })))));
};
export default SwimTimeTrendChart;
